import React, { useState, useCallback } from 'react';
import { X, Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import * as XLSX from 'xlsx';

export const ImportModal = ({ isOpen, onClose, onImport }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [importing, setImporting] = useState(false);
    const [resultMsg, setResultMsg] = useState(null);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const processFile = (selectedFile) => {
        if (!selectedFile) return;
        setFile(selectedFile);
        setResultMsg(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length > 0) {
                const headers = Object.keys(jsonData[0]);
                setPreviewData({
                    rows: jsonData.length,
                    columns: headers,
                    data: jsonData
                });
            }
        };
        reader.readAsArrayBuffer(selectedFile);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    }, []);

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const executeImport = () => {
        if (!previewData) return;
        setImporting(true);

        // Simulate processing delay
        setTimeout(() => {
            const results = onImport(previewData.data);
            setImporting(false);
            setResultMsg(results);
        }, 1000);
    };

    const reset = () => {
        setFile(null);
        setPreviewData(null);
        setResultMsg(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200">
            <div className="bg-stone-900 border border-stone-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-stone-800 flex items-center justify-between bg-stone-950">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-900/20 rounded-lg">
                            <Upload className="text-blue-400" size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Import Data / Update</h3>
                            <p className="text-xs text-stone-400">Update existing or create new products</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-500 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {!previewData && !resultMsg && (
                        <div
                            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-colors ${dragActive ? 'border-blue-500 bg-blue-900/10' : 'border-stone-700 hover:border-stone-600 bg-stone-950'}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <FileSpreadsheet size={48} className="text-stone-600 mb-4" />
                            <p className="text-stone-300 font-medium mb-2">Drag & Drop Excel file here</p>
                            <p className="text-stone-500 text-sm mb-6">or click to browse</p>
                            <label className="px-4 py-2 bg-stone-800 hover:bg-stone-700 text-stone-200 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                                Select File
                                <input type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleChange} />
                            </label>
                        </div>
                    )}

                    {previewData && !resultMsg && (
                        <div className="space-y-4">
                            <div className="bg-stone-950 border border-stone-800 rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <FileSpreadsheet className="text-green-500" size={24} />
                                    <div className="overflow-hidden">
                                        <p className="text-sm font-bold text-white truncate">{file.name}</p>
                                        <p className="text-xs text-stone-500">{(file.size / 1024).toFixed(2)} KB</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="bg-stone-900 p-3 rounded border border-stone-800">
                                        <p className="text-xs text-stone-500 uppercase font-bold">Records Found</p>
                                        <p className="text-xl font-mono text-white">{previewData.rows}</p>
                                    </div>
                                    <div className="bg-stone-900 p-3 rounded border border-stone-800">
                                        <p className="text-xs text-stone-500 uppercase font-bold">Columns Detected</p>
                                        <p className="text-xl font-mono text-white">{previewData.columns.length}</p>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <p className="text-xs text-stone-500 mb-2">Columns: {previewData.columns.slice(0, 5).join(', ')}{previewData.columns.length > 5 ? '...' : ''}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {resultMsg && (
                        <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-6 flex flex-col items-center text-center animate-in zoom-in duration-300">
                            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle className="text-green-400" size={24} />
                            </div>
                            <h4 className="text-lg font-bold text-white mb-2">Import Successful!</h4>
                            <p className="text-stone-300 mb-6">{resultMsg}</p>
                            <button onClick={reset} className="text-sm text-blue-400 hover:text-blue-300 underline">
                                Import another file
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!resultMsg && (
                    <div className="p-6 border-t border-stone-800 bg-stone-950 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg bg-stone-800 text-stone-300 hover:bg-stone-700 transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                        {previewData && (
                            <button
                                onClick={executeImport}
                                disabled={importing}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                {importing ? (
                                    <>Processing...</>
                                ) : (
                                    <>
                                        Import Data
                                        <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
